<template>
    <div class="datepicker-input-group">
        <VueDatePicker 
            v-if="!monthPicker"
            :modelValue="local_date"
            @update:modelValue="updateDate"
            :time-picker="false"
            :enable-time-picker="enableTimePicker"
            :format="inputFormat"
            week-start="0"
            :placeholder="placeholder"
            :input-class="inputClass"
            :class="customClass"
            :position="position"
            auto-apply 
            :time-picker-inline="true"
            :disabled="disabled">
        </VueDatePicker>
        <VueDatePicker v-else
            v-model="local_date"
            :modelValue="local_date"
            @update:modelValue="updateDate"
            :time-picker="false"
            :enable-time-picker="false"
            :format="inputFormat"
            week-start="0"
            :placeholder="placeholder"
            :input-class="inputClass"
            :class="customClass"
            month-picker
            :position="position"
            auto-apply
            
            :disabled="disabled">
        </VueDatePicker>
        <span v-if="enableTimePicker && showTimezone" class="timezone-display ms-2 text-muted small">
            {{ timezoneDisplay }}
        </span>
    </div>
</template>

<script>
import VueDatePicker from '@vuepic/vue-datepicker';
import { defineComponent } from 'vue';

export default defineComponent({
    name: 'DatepickerUtc',
    components: {
        VueDatePicker
    },
    props: {
        placeholder: {
            type: String,
            default: 'Select date'
        },
        modelValue: {
            type: [Date, String],
            default: null
        },
        disabled: {
            type: Boolean,
            default: false
        },
        inputFormat: {
            type: String,
            default: 'MM/dd/yyyy'
        },
        inputClass: {
            type: String,
            default: 'form-control'
        },
        customClass: {
            type: String,
            default: ''
        },
        position: {
            type: String,
            default: 'left'
        },
        monthPicker: {
            type: Boolean,
            default: false
        },
        enableTimePicker: {
            type: Boolean,
            default: false
        },
        showTimezone: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            local_date: null
        }
    },
    computed: {
        timezoneDisplay() {
            if (!this.local_date) return '';
            try {
                // Get timezone abbreviation (e.g., EST, PST)
                const timeZoneName = Intl.DateTimeFormat('en-US', {
                    timeZoneName: 'short'
                }).formatToParts(this.local_date).find(part => part.type === 'timeZoneName')?.value || '';
                
                // Get timezone offset (e.g., GMT-5)
                const offset = -this.local_date.getTimezoneOffset() / 60;
                const offsetStr = offset >= 0 ? `+${offset}` : `${offset}`;
                
                return `${timeZoneName} (GMT${offsetStr})`;
            } catch (e) {
                // Fallback to simple offset
                const offset = -this.local_date.getTimezoneOffset() / 60;
                const offsetStr = offset >= 0 ? `+${offset}` : `${offset}`;
                return `GMT${offsetStr}`;
            }
        }
    },
    watch: {
        modelValue: {
            immediate: true,
            handler(newValue) {
                if (newValue) {
                    // Handle both Date and String inputs
                    const date = newValue instanceof Date ? new Date(newValue) : new Date(newValue);
                    
                    if (this.monthPicker) {
                        // For month picker, create an object with month and year
                        // VueDatePicker expects zero-based month
                        this.local_date = {
                            month: date.getMonth()+1,
                            year: date.getFullYear()
                        };
                    } else {
                        // Adjust for timezone offset to preserve the date
                        const offset = date.getTimezoneOffset();
                        date.setMinutes(date.getMinutes() + offset);
                        this.local_date = date;
                    }
                } else {
                    this.local_date = null;
                }
            }
        }
    },
    methods: {
        updateDate(newDate) {
            if (newDate) {
                let date;
                
                // Handle month picker selection (returns {month, year} object)
                if (typeof newDate === 'object' && newDate.month !== undefined && newDate.year !== undefined) {
                    // Create date for the first day of the selected month
                    date = new Date(newDate.year, newDate.month, 1);
                    // For month picker, don't apply timezone conversion since we're dealing with month/year abstraction
                    this.$emit('update:modelValue', date);
                    } else {
                        // Handle regular date/datetime selection
                        date = new Date(newDate);
                        // Convert local date/datetime back to UTC
                        // This preserves the exact time the user selected
                        const offset = date.getTimezoneOffset();
                        date.setMinutes(date.getMinutes() - offset);
                        this.$emit('update:modelValue', date);
                    }
            } else {
                this.$emit('update:modelValue', null);
            }
        },
        clearDate() {
            this.local_date = null;
            this.$emit('update:modelValue', null);
        }
    }
})
</script>